import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtTime } from "@/lib/datetime";
import { Stethoscope } from "lucide-react";

/**
 * PODDiagnosticsPanel — one-click storage smoke test + recent deliver-failure
 * viewer. Lives on the Audit Log tab (Super Admin / Dispatcher).
 *
 * - "Test storage" hits `/api/admin/debug/storage-health` and reports the
 *   round-trip in ms + any error. Failing here explains a driver "1 upload
 *   pending" pill that never clears.
 * - "Show recent deliver failures" hits `/api/admin/debug/deliver-errors` and
 *   renders the last 20 failures inline (timestamp, HTTP code, driver, order,
 *   detail).
 */
export default function PODDiagnosticsPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [errBusy, setErrBusy] = useState(false);
  const [errs, setErrs] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get("/admin/debug/storage-health");
      setResult(data);
      if (data.ok) toast.success(`Storage OK · roundtrip ${data.roundtrip_ms} ms`);
      else toast.error(`Storage FAIL · ${data.error || "unknown"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setResult({ ok: false, error: detail });
      toast.error(`Probe failed · ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  const loadErrs = async () => {
    setErrBusy(true);
    try {
      const { data } = await api.get("/admin/debug/deliver-errors", { params: { limit: 20 } });
      setErrs(Array.isArray(data) ? data : []);
      toast.success(`Loaded ${data.length} recent deliver failure${data.length === 1 ? "" : "s"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Load failed · ${detail}`);
    } finally {
      setErrBusy(false);
    }
  };

  const okColor = result?.ok ? "text-emerald-600" : "text-destructive";

  return (
    <Card className="p-4 flex flex-col gap-3" data-testid="storage-health-card">
      <div className="flex items-center gap-2 pb-1 border-b border-border/60">
        <Stethoscope className="h-4 w-4 text-accent" />
        <h3 className="text-sm font-semibold">POD delivery diagnostics</h3>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground">
            Test the object store used for POD photos, and pull the last 20 deliver failures across all drivers.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={run}
          disabled={busy}
          data-testid="storage-health-run-btn"
        >
          {busy ? "Testing…" : "Test storage"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={loadErrs}
          disabled={errBusy}
          data-testid="deliver-errors-run-btn"
        >
          {errBusy ? "Loading…" : "Show recent deliver failures"}
        </Button>
      </div>
      {result && (
        <div className={`text-xs font-mono-num ${okColor}`} data-testid="storage-health-result">
          {result.ok ? "OK" : "FAIL"}
          {result.roundtrip_ms != null && ` · ${result.roundtrip_ms} ms`}
          {result.error && ` · ${result.error}`}
          {result.app_name && ` · app=${result.app_name}`}
        </div>
      )}
      {errs && (
        <div className="mt-1 border-t pt-2" data-testid="deliver-errors-list">
          {errs.length === 0 ? (
            <div className="text-xs text-emerald-600">
              No recorded deliver failures — every /deliver call has succeeded.
            </div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-auto">
              {errs.map((e) => (
                <div
                  key={e.id}
                  className="text-xs font-mono-num flex flex-wrap gap-x-2"
                  data-testid="deliver-error-row"
                >
                  <span className="text-muted-foreground">{fmtTime(e.at)}</span>
                  <span className="font-semibold text-destructive">{e.status_code}</span>
                  <span>{e.user_email || e.user_id}</span>
                  <span className="text-muted-foreground">→</span>
                  <span>order {e.order_id?.slice(0, 8)}…</span>
                  <span className="basis-full pl-4">{e.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
